import React, { useState, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { GlobalContext } from '../context/GlobalContext';
import { triggerLogout } from '../hooks/useAuth';
import { 
  MagnifyingGlassIcon, 
  BellIcon, 
  ChevronDownIcon,
  UserCircleIcon,
  CogIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';

const AppHeader = ({ sidebarCollapsed = false }) => {
  const { cinfo, uinfo } = useContext(GlobalContext);
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Dynamic title based on current route
  const getPageTitle = () => {
    const path = location.pathname;
    
    // Main sections
    if (path === '/') return 'Dashboard';
    if (path === '/dashboard') return 'Dashboard';
    if (path === '/messenger') return 'Messenger';
    if (path === '/sales') return 'Sales Reports';
    if (path === '/customers') return 'Customer Management';
    if (path === '/repvisits') return 'BDM Manager';
    if (path === '/repvisits/logs') return 'Rep Visit Log';
    if (path === '/fleet') return 'Fleet Management';
    if (path === '/fleet/vehicles') return 'Fleet Vehicles';
    if (path === '/fleet/drivers') return 'Fleet Drivers';
    if (path === '/fleet/maintenance') return 'Fleet Maintenance';
    if (path === '/reports') return 'Reports';
    if (path === '/settings') return 'Settings';
    if (path === '/profile') return 'Profile';
    
    // Nested routes
    if (path.startsWith('/sales/')) return 'Sales Reports';
    if (path.startsWith('/customers/')) return 'Customer Management';
    if (path.startsWith('/fleet/')) return 'Fleet Management';
    if (path.startsWith('/reports/')) return 'Reports';
    
    // Default fallback
    return 'Sales Assist';
  };

  // Get breadcrumb context for nested routes
  const getBreadcrumb = () => {
    const path = location.pathname;
    
    if (path === '/fleet/vehicles') return 'Fleet Management';
    if (path === '/fleet/drivers') return 'Fleet Management';
    if (path === '/fleet/maintenance') return 'Fleet Management';
    if (path.startsWith('/sales/')) return 'Sales';
    if (path.startsWith('/customers/')) return 'Customers';
    if (path.startsWith('/reports/')) return 'Reports';
    
    return null;
  };

  const handleLogout = () => {
    triggerLogout();
    window.location.href = '/login';
  };

  return (
    <header className={`fixed top-0 right-0 z-10 bg-white shadow-sm border-b border-gray-200 transition-all duration-300 ${sidebarCollapsed ? 'left-[70px]' : 'left-[260px]'}`}>
      <div className="w-full px-6">
        <div className="flex justify-between items-center h-16">
          {/* Left side - Dynamic Title */}
          <div className="flex-shrink-0">
            <div className="flex items-center space-x-2">
              <h1 className="text-xl font-semibold text-gray-900">
                {getPageTitle()}
              </h1>
              {getBreadcrumb() && (
                <>
                  <span className="text-gray-400">/</span>
                  <span className="text-sm text-gray-500">{getBreadcrumb()}</span>
                </>
              )}
            </div>
          </div>

          {/* Center - Search Bar */}
          <div className="flex-1 max-w-lg mx-8">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Right side - Notifications and User Menu */}
          <div className="flex items-center space-x-4">
            {/* Notifications */}
            <button className="p-2 text-gray-400 hover:text-gray-500 relative">
              <BellIcon className="h-6 w-6" />
              <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-400 ring-2 ring-white"></span>
            </button>

            {/* User Menu Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-3 text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {uinfo?.Name?.charAt(0) || uinfo?.Username?.charAt(0) || 'U'}
                  </span>
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium text-gray-900">
                    {uinfo?.Name || uinfo?.Username || 'User'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {uinfo?.Status || 'User'}
                  </p>
                </div>
                <ChevronDownIcon className="h-4 w-4 text-gray-400" />
              </button>

              {/* Dropdown Menu */}
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">
                      {uinfo?.Name || uinfo?.Username || 'User'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {cinfo?.CompanyName || 'Company'}
                    </p>
                  </div>
                  
                  <a
                    href="#"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <UserCircleIcon className="h-4 w-4 mr-3" />
                    Profile
                  </a>
                  
                  <a
                    href="#"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <CogIcon className="h-4 w-4 mr-3" />
                    Settings
                  </a>
                  
                  <div className="border-t border-gray-100">
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3" />
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default AppHeader;